import { useCallback, useMemo, useState } from 'react';

import { Table as TanstackTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { DataTableColumnsSelector } from '@/components/data-table/data-table-columns-selector';
import {
  parseDateRange,
  serializeDateRange,
} from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { DataTableFiltersV2 } from '@/components/data-table/filters-v2';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useColumnOrder } from '@/hooks/use-column-order';
import { useColumnVisibility } from '@/hooks/use-column-visibility';
import { useDayRollover } from '@/hooks/use-day-rollover';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { TableIds } from '@/lib/constants/table-ids';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/modules/common';
import {
  DateRangePickerV2,
  DateRangePreset,
  DateRangeV2,
  detectPresetFromDates,
  getDateRangeFromPreset,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { PageTemplate } from '@/modules/common/components/templates/page-template';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { UnsavedChangesModal } from '@/modules/settings/components/unsaved-changes-modal';

import {
  ApprovalsSummarySection,
  ApprovalsSummaryToggleButton,
} from '../components/approvals-summary/approvals-summary-cards';
import { ApprovalConfirmModal } from '../components/modals/approval-confirm-modal';
import { TimeEntriesTable } from '../components/time-entries-table';
import {
  APPROVALS_FILTER_KEY_DATE_RANGE,
  APPROVALS_TABLE_FILTER_KEYS,
  APPROVALS_TABLE_FILTERS_STORAGE_KEY,
} from '../constants/approvals-filter-keys';
import { hasAnyInvalidApprovedInput } from '../helpers/invalid-approved-inputs.helper';
import { useApprovals } from '../hooks/use-approvals';
import { useApprovalsStore } from '../store/approvals.store';
import { GetApprovalFilterRequest } from '../types/dto/worklogs-approval.dto';
import {
  ApprovalConfirmMode,
  ApprovalProjectRow,
  ApprovalSelection,
  ApprovalSelectionLevel,
  ApprovalTimeEntry,
} from '../types/time-tracking-approvals';

/** Persisted collapsed/expanded state of the summary stat cards. */
const SUMMARY_COLLAPSED_STORAGE_KEY = 'time-tracking-approvals-summary-collapsed';

/**
 * Stand-in filter for the render before the date range resolves. A module constant, not an
 * inline literal: the table and the section loaders key work off the filter, so a fresh object
 * per render would make them redo it every time.
 */
const EMPTY_APPROVAL_FILTER: GetApprovalFilterRequest = { startDate: '', endDate: '' };

export const TimeTrackingApprovalsPage = () => {
  const { t } = useTranslation(['authenticated.timeTracking', 'authenticated.timeOff']);

  const defaultDateRange = useMemo(
    () => serializeDateRange(getDateRangeFromPreset(DateRangePreset.LastMonth)),
    []
  );

  usePersistedTableFilters({
    storageKey: APPROVALS_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: APPROVALS_TABLE_FILTER_KEYS,
    defaultFilters: { [APPROVALS_FILTER_KEY_DATE_RANGE]: defaultDateRange },
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const { dates, selectedPreset } = useMemo(() => {
    const paramValue = searchParams.get(APPROVALS_FILTER_KEY_DATE_RANGE);
    const parsed = paramValue ? parseDateRange(paramValue) : null;
    const resolvedDates = parsed ?? getDateRangeFromPreset(DateRangePreset.LastMonth);
    return { dates: resolvedDates, selectedPreset: detectPresetFromDates(resolvedDates) };
  }, [searchParams]);

  const handleDateChange = (newDates: DateRangeV2) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev.toString());
        next.set(APPROVALS_FILTER_KEY_DATE_RANGE, serializeDateRange(newDates));
        return next;
      },
      { replace: true }
    );
  };

  const handlePresetSelect = (preset: DateRangePreset) => {
    handleDateChange(getDateRangeFromPreset(preset));
  };

  // If the tab stays open past a calendar boundary (day, week, quarter, year)
  // and an active preset is selected, re-resolve the preset so the range
  // tracks the current day/week/quarter/year instead of the previous one.
  useDayRollover(() => {
    if (selectedPreset) {
      handleDateChange(getDateRangeFromPreset(selectedPreset));
    }
  });

  const {
    rowSelection,
    editedEntryValues,
    invalidApprovedInputs,
    clearAllEdits,
    clearRowSelection,
  } = useApprovalsStore();
  const isSelectedRows = Object.keys(rowSelection).length > 0;

  // Unapproved inline edits make the page dirty — warn before navigating away. An approved
  // input holding unparseable text counts too: it is work the user typed that was never
  // committed, and the store outlives this page, so leaving without discarding it would keep
  // every approve action blocked by a row that is no longer anywhere on screen.
  const hasUnsavedEdits =
    Object.keys(editedEntryValues).length > 0 || hasAnyInvalidApprovedInput(invalidApprovedInputs);
  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: hasUnsavedEdits,
    onDiscard: () => {
      clearAllEdits();
      clearRowSelection();
    },
  });

  const { hasPermission } = usePermissions();
  const canApprove = hasPermission(OpsiumPolicy.WorklogsApprove);
  const canViewBillingRates = hasPermission(OpsiumPolicy.ProjectsBillingManage);

  const {
    data,
    summary,
    totalWorklogs,
    filtersConfig,
    filterState,
    isLoading,
    isSummaryLoading,
    isWorklogsLoading,
    filter: approvalFilter,
    fetchMoreProjects,
    hasMoreProjects,
    isFetchingMoreProjects,
  } = useApprovals(dates);

  const filter = approvalFilter ?? EMPTY_APPROVAL_FILTER;

  // Summary cards collapse is an explicit user choice (toggle button in the summary
  // header), persisted across sessions — no scroll-driven behavior.
  const [isSummaryCollapsed, setIsSummaryCollapsed] = useLocalStorage(
    SUMMARY_COLLAPSED_STORAGE_KEY,
    false
  );

  const { columnVisibility, onColumnVisibilityChange } = useColumnVisibility(
    TableIds.TIME_TRACKING_APPROVALS
  );

  const { columnOrder, onColumnOrderChange } = useColumnOrder(TableIds.TIME_TRACKING_APPROVALS);

  const [table, setTable] = useState<TanstackTable<ApprovalProjectRow> | null>(null);

  // Modal state — the selection (projects / members / worklogs) pending confirmation.
  const [approveSelection, setApproveSelection] = useState<ApprovalSelection | null>(null);
  const [unapproveSelection, setUnapproveSelection] = useState<ApprovalSelection | null>(null);

  const toWorklogSelection = (entry: ApprovalTimeEntry): ApprovalSelection => ({
    level: ApprovalSelectionLevel.Worklogs,
    entries: [entry],
  });

  // Stable identities: these feed the table's column definitions, which are memoized on them.
  // Inline arrows would rebuild every column instance on each page render (and this page
  // re-renders on every selection change).
  const handleApproveEntry = useCallback(
    (entry: ApprovalTimeEntry) => setApproveSelection(toWorklogSelection(entry)),
    []
  );
  const handleRevertEntry = useCallback(
    (entry: ApprovalTimeEntry) => setUnapproveSelection(toWorklogSelection(entry)),
    []
  );
  const handleBulkApprove = useCallback(
    (selection: ApprovalSelection) => setApproveSelection(selection),
    []
  );
  const handleBulkRevert = useCallback(
    (selection: ApprovalSelection) => setUnapproveSelection(selection),
    []
  );

  return (
    <PageTemplate
      title={t('approvals.title')}
      useStickyHeader
      className="min-w-[540px] p-0"
      headerClassName="relative z-10 items-start bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]"
      contentClassName="relative"
      rightActions={
        <DateRangePickerV2
          dates={dates}
          onChange={handleDateChange}
          selectedPreset={selectedPreset}
          onPresetSelect={handlePresetSelect}
        />
      }
      secondaryActions={
        <ApprovalsSummaryToggleButton
          collapsed={isSummaryCollapsed}
          onToggleCollapsed={() => setIsSummaryCollapsed(!isSummaryCollapsed)}
        />
      }
    >
      <Separator className="ml-[-16px] w-[calc(100%+32px)]" />
      <div
        className={cn(
          'flex flex-col gap-4 p-4',
          isSelectedRows && canApprove
            ? 'max-h-[calc(100vh-65px-73px)]'
            : 'max-h-[calc(100vh-65px)]'
        )}
      >
        <ApprovalsSummarySection
          summary={summary}
          isLoading={isSummaryLoading}
          collapsed={isSummaryCollapsed}
          showIncomeCard={canViewBillingRates}
        />
        <div className="flex flex-1 flex-col gap-3 overflow-auto rounded-lg border p-4">
          <h2 className="text-lg font-semibold">{t('approvals.entries.title')}</h2>
          <div className="flex items-start justify-between gap-2">
            <DataTableFiltersV2
              filtersConfig={filtersConfig}
              filterState={filterState}
              tableId="time-tracking-approvals"
              showSaveButton={false}
              className="flex flex-wrap gap-2"
            />
            {table && (
              <DataTableColumnsSelector
                table={table}
                columnOrder={columnOrder}
                onColumnOrderChange={onColumnOrderChange}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={onColumnVisibilityChange}
              />
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('approvals.entries.allTimeTracksCountPrefix')}
            {isWorklogsLoading ? (
              <Skeleton className="ml-1 inline-block h-3 w-6 bg-foreground/15" />
            ) : (
              totalWorklogs
            )}
          </p>
          <TimeEntriesTable
            data={data ?? []}
            filter={filter}
            isLoading={isLoading}
            hasMoreProjects={hasMoreProjects}
            isFetchingMoreProjects={isFetchingMoreProjects}
            onLoadMoreProjects={fetchMoreProjects}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            columnOrder={columnOrder}
            onColumnOrderChange={onColumnOrderChange}
            onTableReady={setTable}
            onApprove={handleApproveEntry}
            onBulkApprove={handleBulkApprove}
            onRevert={handleRevertEntry}
            onBulkRevert={handleBulkRevert}
          />
        </div>
      </div>

      <ApprovalConfirmModal
        mode={ApprovalConfirmMode.Approve}
        open={approveSelection !== null}
        onOpen={() => {}}
        onClose={() => setApproveSelection(null)}
        selection={approveSelection}
        filter={filter}
      />

      <ApprovalConfirmModal
        mode={ApprovalConfirmMode.Unapprove}
        open={unapproveSelection !== null}
        onOpen={() => {}}
        onClose={() => setUnapproveSelection(null)}
        selection={unapproveSelection}
        filter={filter}
      />

      <UnsavedChangesModal
        open={unsavedChangesGuard.isModalOpen}
        onClose={unsavedChangesGuard.handleModalCancel}
        onDiscard={unsavedChangesGuard.handleModalDiscard}
      />
    </PageTemplate>
  );
};
